import type { CatalogCard } from "@pokifolio/contracts";
import { Loader2, RefreshCw, Replace, TextSearch } from "lucide-react";

import { Button } from "../../components/ui/button";
import { Card } from "../../components/ui/card";
import { CardImage } from "./card-image";

/** What is being searched: one exact card, or the words as typed. */
export type SearchTarget =
  { readonly kind: "card"; readonly card: CatalogCard } | { readonly kind: "words"; readonly words: string };

interface SearchedCardBarProps {
  readonly target: SearchTarget;
  /** The search is running now. */
  readonly searching: boolean;
  readonly onSearchAgain: () => void;
  readonly onChangeCard: () => void;
}

/** The chosen card in a compact bar above its results; the grid of matching cards folds away. */
export function SearchedCardBar({ target, searching, onSearchAgain, onChangeCard }: SearchedCardBarProps) {
  return (
    <Card className="flex flex-wrap items-center gap-4 p-4">
      {target.kind === "card" ? (
        <div className="w-14 shrink-0">
          <CardImage src={target.card.imageSmall} />
        </div>
      ) : (
        <div className="flex size-14 shrink-0 items-center justify-center rounded-md bg-muted">
          <TextSearch className="size-6 text-muted-foreground" aria-hidden="true" />
        </div>
      )}
      <div className="min-w-0 flex-1">
        <p className="text-xs font-medium uppercase tracking-wide text-muted-foreground">
          {target.kind === "card" ? "Searched card" : "Searched words"}
        </p>
        <p className="truncate text-lg font-semibold text-foreground">
          {target.kind === "card" ? `${target.card.name} ${target.card.number}` : `“${target.words}”`}
        </p>
        {target.kind === "card" && <p className="text-sm text-muted-foreground">{target.card.setName}</p>}
      </div>
      <div className="flex flex-wrap items-center gap-2">
        {searching ? (
          <span className="inline-flex items-center gap-2 text-sm text-muted-foreground">
            <Loader2 className="size-4 animate-spin" aria-hidden="true" />
            Searching…
          </span>
        ) : (
          <Button variant="ghost" size="sm" onClick={onSearchAgain}>
            <RefreshCw aria-hidden="true" />
            Search again
          </Button>
        )}
        <Button variant="secondary" size="sm" onClick={onChangeCard} disabled={searching}>
          <Replace aria-hidden="true" />
          Change card
        </Button>
      </div>
    </Card>
  );
}
